import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Chart } from 'react-google-charts';
import { FileText, Users, DollarSign, Gift, TrendingDown, CheckCircle, AlertTriangle, Info, Lock } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { isProOrHigher } from '../utils/tierHelpers';
import './Analytics.css';

const Analytics = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [selectedCommitteeId, setSelectedCommitteeId] = useState(null);
  const [committeeFilter, setCommitteeFilter] = useState('');
  const [selectedCycle, setSelectedCycle] = useState({ type: null, year: null });
  const [cycleTableView, setCycleTableView] = useState('april'); // 'april' or 'november'
  const [showCycleHelp, setShowCycleHelp] = useState(false);
  const [committeeReports, setCommitteeReports] = useState(null);
  const [loadingReports, setLoadingReports] = useState(false);
  const [showDonorTooltip, setShowDonorTooltip] = useState(false);
  const [showCashDonorTooltip, setShowCashDonorTooltip] = useState(false);
  const [showInKindDonorTooltip, setShowInKindDonorTooltip] = useState(false);
  const donorTooltipRef = useRef(null);
  const cashDonorTooltipRef = useRef(null);
  const inKindDonorTooltipRef = useRef(null);
  const [analysisScope, setAnalysisScope] = useState('single'); // 'single' or 'group'
  const [userGroups, setUserGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupCommitteeIds, setGroupCommitteeIds] = useState([]);

  useEffect(() => {
    fetchAnalytics();
  }, [selectedCycle]);

  // Fetch user groups when component mounts (Pro users only)
  useEffect(() => {
    if (isProOrHigher(user)) {
      fetchUserGroups();
    }
  }, [user]);

  // When group is selected, fetch group details to get member committee IDs
  useEffect(() => {
    if (analysisScope === 'group' && selectedGroupId) {
      fetchGroupDetails();
    }
  }, [selectedGroupId, analysisScope]);

  useEffect(() => {
    if (selectedCommitteeId) {
      fetchCommitteeReports();
    }
  }, [selectedCommitteeId, selectedCycle, groupCommitteeIds, analysisScope]);

  // Click outside handlers for tooltips
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (donorTooltipRef.current && !donorTooltipRef.current.contains(event.target)) {
        setShowDonorTooltip(false);
      }
      if (cashDonorTooltipRef.current && !cashDonorTooltipRef.current.contains(event.target)) {
        setShowCashDonorTooltip(false);
      }
      if (inKindDonorTooltipRef.current && !inKindDonorTooltipRef.current.contains(event.target)) {
        setShowInKindDonorTooltip(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Ensure a committee is always selected, prioritizing URL query param
  useEffect(() => {
    if (analyticsData?.committees && analyticsData.committees.length > 0 && !selectedCommitteeId) {
      const committeeParam = searchParams.get('committee');
      if (committeeParam) {
        // Check if the committee ID from URL exists in the data
        const committeeExists = analyticsData.committees.some(c => c.committee_id === committeeParam);
        if (committeeExists) {
          setSelectedCommitteeId(committeeParam);
        } else {
          setSelectedCommitteeId(analyticsData.committees[0].committee_id);
        }
      } else {
        setSelectedCommitteeId(analyticsData.committees[0].committee_id);
      }
    }
  }, [analyticsData, selectedCommitteeId, searchParams]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      // Build query params
      const params = {};
      if (selectedCycle.type && selectedCycle.year) {
        params.cycle_type = selectedCycle.type;
        params.cycle_year = selectedCycle.year;
      }

      const response = await api.get('/api/analytics/overview', { params });
      setAnalyticsData(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      if (err.response?.status === 403) {
        setError('Analytics dashboard is only available to Basic and Pro tier users. Please upgrade your plan.');
      } else {
        setError(err.response?.data?.detail || 'Failed to load analytics data');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCommitteeReports = async () => {
    if (!selectedCommitteeId) return;

    try {
      setLoadingReports(true);
      const params = {};
      if (selectedCycle.type && selectedCycle.year) {
        params.cycle_type = selectedCycle.type;
        params.cycle_year = selectedCycle.year;
      }

      if (analysisScope === 'group' && groupCommitteeIds.length > 0) {
        // Group mode: fetch reports for all entities in parallel and aggregate
        const reportPromises = groupCommitteeIds.map(committeeId =>
          api.get(`/api/analytics/committee/${committeeId}/reports`, { params })
            .then(res => ({ committeeId, data: res.data }))
            .catch(err => {
              console.error(`Error fetching reports for committee ${committeeId}:`, err);
              return { committeeId, data: null };
            })
        );

        const allReports = await Promise.all(reportPromises);

        // Store as group reports with per-entity data
        const validReports = allReports.filter(r => r.data !== null);
        setCommitteeReports({
          mode: 'group',
          by_entity: validReports.reduce((acc, { committeeId, data }) => {
            acc[committeeId] = data;
            return acc;
          }, {})
        });
      } else {
        // Single mode: existing behavior
        const response = await api.get(`/api/analytics/committee/${selectedCommitteeId}/reports`, { params });
        setCommitteeReports(response.data);
      }
    } catch (err) {
      console.error('Error fetching committee reports:', err);
      setCommitteeReports(null);
    } finally {
      setLoadingReports(false);
    }
  };

  const fetchUserGroups = async () => {
    try {
      const response = await api.get('/api/groups');
      setUserGroups(response.data);
    } catch (err) {
      console.error('Error fetching groups:', err);
      setUserGroups([]);
    }
  };

  const fetchGroupDetails = async () => {
    if (!selectedGroupId) return;

    try {
      const response = await api.get(`/api/groups/${selectedGroupId}`);
      setSelectedGroup(response.data);

      // Extract committee IDs from members
      const committeeIds = response.data.members.map(m => m.committee_id);
      setGroupCommitteeIds(committeeIds);

      // Trigger reports fetch for all entities
      if (committeeIds.length > 0) {
        fetchCommitteeReports();
      }
    } catch (err) {
      console.error('Error fetching group details:', err);
      setSelectedGroup(null);
      setGroupCommitteeIds([]);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatUSD = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  // Aggregation helpers for group mode
  const parseNum = (val) => {
    if (val === null || val === undefined || val === '') return 0;
    const num = parseFloat(val);
    return isNaN(num) ? 0 : num;
  };

  const aggregateTopDonors = (groupCommittees, field) => {
    const donorMap = new Map();

    groupCommittees.forEach(committee => {
      const donors = committee[field] || [];
      donors.forEach(donor => {
        const key = donor.name.toLowerCase().trim();
        const amount = parseNum(donor.amount || donor.total_amount);
        if (donorMap.has(key)) {
          const existing = donorMap.get(key);
          existing.amount += amount;
          existing.total_amount = existing.amount; // Keep both for compatibility
        } else {
          donorMap.set(key, {
            name: donor.name,
            amount: amount,
            total_amount: amount
          });
        }
      });
    });

    return Array.from(donorMap.values())
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 10);
  };

  const aggregateTopExpenses = (groupCommittees) => {
    const payeeMap = new Map();

    groupCommittees.forEach(committee => {
      const expenses = committee.top_expenses_by_payee || [];
      expenses.forEach(expense => {
        const key = expense.payee.toLowerCase().trim();
        const amount = parseNum(expense.amount || expense.total_amount);
        if (payeeMap.has(key)) {
          const existing = payeeMap.get(key);
          existing.amount += amount;
          existing.total_amount = existing.amount; // Keep both for compatibility
        } else {
          payeeMap.set(key, {
            payee: expense.payee,
            amount: amount,
            total_amount: amount
          });
        }
      });
    });

    return Array.from(payeeMap.values())
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 10);
  };

  const aggregateTopExpensesByCategory = (groupCommittees) => {
    const categoryMap = new Map();

    groupCommittees.forEach(committee => {
      const categories = committee.top_expenses_by_category || [];
      categories.forEach(expense => {
        const key = expense.category.toLowerCase().trim();
        const amount = parseNum(expense.amount || expense.total_amount);
        const count = expense.expense_count || 0;
        if (categoryMap.has(key)) {
          const existing = categoryMap.get(key);
          existing.total_amount += amount;
          existing.expense_count += count;
        } else {
          categoryMap.set(key, {
            category: expense.category,
            total_amount: amount,
            expense_count: count
          });
        }
      });
    });

    return Array.from(categoryMap.values())
      .sort((a, b) => b.total_amount - a.total_amount)
      .slice(0, 10);
  };

  const aggregateSankeyData = (groupCommittees) => {
    // Group mode Sankey: Each entity gets its own Cash and In-Kind vertical bars
    // Just like single entity mode but with multiple entities side by side
    const tooltipColumn = { role: 'tooltip', type: 'string', p: { html: true } };
    const data = [['From', 'To', 'Amount', tooltipColumn]];
    const buildTooltip = (from, to, amount) => (
      `<div><strong>${from} → ${to}</strong><br/>Amount: <strong>${formatUSD(amount)}</strong></div>`
    );
    const pushRow = (from, to, amount) => {
      const value = parseNum(amount);
      if (value > 0) {
        data.push([from, to, value, buildTooltip(from, to, value)]);
      }
    };
    const middleLabel = (name, kind) => `${kind} — ${name}`;

    // Sort committees by name for consistent ordering
    const sortedCommittees = [...groupCommittees].sort((a, b) =>
      a.committee_name.localeCompare(b.committee_name)
    );

    sortedCommittees.forEach(committee => {
      if (!committee.sankey) return;

      const entityName = committee.committee_name;

      // Each entity gets its own Cash and In-Kind nodes in the middle
      const cashNode = middleLabel(entityName, 'Cash');
      const inKindNode = middleLabel(entityName, 'In-Kind');

      // API returns: top_cash_donors, top_in_kind_donors, top_expense_payees
      const cashDonors = committee.sankey.top_cash_donors || [];
      const inKindDonors = committee.sankey.top_in_kind_donors || [];
      const expensePayees = committee.sankey.top_expense_payees || [];

      // Cash donor flows -> Entity's Cash node
      cashDonors.forEach(donor => {
        if (donor && donor.name && donor.amount > 0) {
          pushRow(donor.name, cashNode, donor.amount);
        }
      });

      // Other cash donations
      if (committee.sankey.other_cash_donations > 0) {
        pushRow('Other Donors', cashNode, committee.sankey.other_cash_donations);
      }

      // Loans to cash (if any)
      if (committee.sankey.total_loans > 0) {
        pushRow('Loans', cashNode, committee.sankey.total_loans);
      }

      // Entity's Cash node -> Expenses
      expensePayees.forEach(expense => {
        if (expense && expense.name && expense.amount > 0) {
          pushRow(cashNode, expense.name, expense.amount);
        }
      });

      // Other expenses
      if (committee.sankey.other_expenses > 0) {
        pushRow(cashNode, 'Other Expenses', committee.sankey.other_expenses);
      }

      // Cash balance
      if (committee.sankey.net_cash_balance > 0) {
        pushRow(cashNode, 'Cash Balance', committee.sankey.net_cash_balance);
      }

      // In-kind flows (if any)
      if (inKindDonors.length > 0 || committee.sankey.other_in_kind_donations > 0) {
        inKindDonors.forEach(donor => {
          if (donor && donor.name && donor.amount > 0) {
            pushRow(donor.name, inKindNode, donor.amount);
          }
        });

        if (committee.sankey.other_in_kind_donations > 0) {
          pushRow('Other In-Kind Donors', inKindNode, committee.sankey.other_in_kind_donations);
        }

        // In-kind flows to terminal node (non-cash value)
        const inKindTotal = parseNum(committee.sankey.total_in_kind_donations);
        if (inKindTotal > 0) {
          pushRow(inKindNode, 'In-Kind', inKindTotal);
        }
      }
    });

    // Return in format compatible with both single and group modes
    return {
      _isGroupMode: true,
      _chartData: data,
      // Dummy values for hasData check
      top_cash_donors: data.length > 1 ? [{ name: 'dummy' }] : [],
      top_in_kind_donors: [],
      top_expense_payees: []
    };
  };

  const aggregateCycleBreakdown = (groupCommittees) => {
    if (!groupCommittees.length) return null;

    // Merge cycle breakdowns from all committees
    // Structure: { april: [{year, ...}, ...], november: [{year, ...}, ...] }
    const aprilMap = new Map();
    const novemberMap = new Map();

    groupCommittees.forEach(committee => {
      if (!committee.cycle_breakdown) return;

      // Process April cycles
      if (committee.cycle_breakdown.april) {
        committee.cycle_breakdown.april.forEach(cycle => {
          const year = cycle.year;
          if (!aprilMap.has(year)) {
            aprilMap.set(year, {
              year,
              reports: 0,
              donors: 0,
              cash_donations: 0,
              in_kind_donations: 0,
              loans: 0,
              total_donations: 0,
              total_expenses: 0,
              net_balance: 0
            });
          }

          const existing = aprilMap.get(year);
          existing.reports += cycle.reports || 0;
          existing.donors += cycle.donors || 0;
          existing.cash_donations += parseNum(cycle.cash_donations);
          existing.in_kind_donations += parseNum(cycle.in_kind_donations);
          existing.loans += parseNum(cycle.loans);
          existing.total_donations += parseNum(cycle.total_donations);
          existing.total_expenses += parseNum(cycle.total_expenses);
          existing.net_balance += parseNum(cycle.net_balance);
        });
      }

      // Process November cycles
      if (committee.cycle_breakdown.november) {
        committee.cycle_breakdown.november.forEach(cycle => {
          const year = cycle.year;
          if (!novemberMap.has(year)) {
            novemberMap.set(year, {
              year,
              reports: 0,
              donors: 0,
              cash_donations: 0,
              in_kind_donations: 0,
              loans: 0,
              total_donations: 0,
              total_expenses: 0,
              net_balance: 0
            });
          }

          const existing = novemberMap.get(year);
          existing.reports += cycle.reports || 0;
          existing.donors += cycle.donors || 0;
          existing.cash_donations += parseNum(cycle.cash_donations);
          existing.in_kind_donations += parseNum(cycle.in_kind_donations);
          existing.loans += parseNum(cycle.loans);
          existing.total_donations += parseNum(cycle.total_donations);
          existing.total_expenses += parseNum(cycle.total_expenses);
          existing.net_balance += parseNum(cycle.net_balance);
        });
      }
    });

    return {
      april: Array.from(aprilMap.values()).sort((a, b) => b.year - a.year),
      november: Array.from(novemberMap.values()).sort((a, b) => b.year - a.year)
    };
  };

  // Helper to render the integrity table (reused for single and per-entity in group mode)
  const renderIntegrityTable = (reports, reportsData) => (
    <>
      <div className="table-container" style={{ overflowX: 'auto' }}>
        <table className="analytics-table" style={{ fontSize: '0.85rem', width: '100%' }}>
          <thead>
            <tr>
              <th style={{ minWidth: '140px' }}>Report Type</th>
              <th style={{ minWidth: '120px' }}>Period</th>
              <th style={{ minWidth: '85px' }}>Start</th>
              <th style={{ minWidth: '85px' }}>Income</th>
              <th style={{ minWidth: '85px' }}>Expenses</th>
              <th style={{ minWidth: '85px' }}>End</th>
              <th style={{ minWidth: '85px' }}>Expected</th>
              <th style={{ minWidth: '110px' }}>Internal Δ</th>
              <th style={{ minWidth: '110px' }}>Prior Δ</th>
              <th style={{ minWidth: '95px' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {reports.map((report, index) => {
              const isOwned = report.is_owned;
              const isMatching = report.matches_report_id != null;
              const matchingReport = isMatching ? reports.find(r => r.report_id === report.matches_report_id) : null;

              let rowStyle = {};
              if (isMatching) {
                rowStyle = { backgroundColor: '#fef3c7', opacity: 0.7, fontStyle: 'italic' };
              } else if (!isOwned) {
                rowStyle = { backgroundColor: '#f3f4f6', opacity: 0.6, cursor: 'not-allowed' };
              }

              return (
                <tr key={report.report_id} style={rowStyle}>
                  <td style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                    {isOwned ? report.report_type : '—'}
                  </td>
                  <td>
                    <div style={{ fontSize: '0.85rem', fontWeight: '500' }}>
                      {report.period_start} to {report.period_end}
                    </div>
                    {isMatching && matchingReport && (
                      <div style={{ fontSize: '0.75rem', color: '#92400e', marginTop: '0.25rem' }}>
                        📋 Matches: {matchingReport.report_type}
                      </div>
                    )}
                    {report.has_date_gap && index > 0 && !isMatching && (
                      <div style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.25rem' }}>
                        ⚠️ {report.days_gap} day gap from prior report
                      </div>
                    )}
                    {!isOwned && !isMatching && (
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                        Not owned
                      </div>
                    )}
                  </td>
                  <td className="amount">{isOwned ? formatCurrency(report.beginning_balance) : '—'}</td>
                  <td className="amount">{isOwned ? formatCurrency(report.receipts) : '—'}</td>
                  <td className="amount">{isOwned ? formatCurrency(report.disbursements) : '—'}</td>
                  <td className="amount">{isOwned ? formatCurrency(report.ending_balance) : '—'}</td>
                  <td className="amount" style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                    {isOwned ? formatCurrency(report.expected_ending) : '—'}
                  </td>
                  <td className={report.has_internal_issue ? 'negative' : ''} style={{ fontWeight: report.has_internal_issue ? 'bold' : 'normal' }}>
                    {isOwned ? (
                      report.has_internal_issue ? (
                        <span style={{ color: '#dc2626' }}>{formatCurrency(report.internal_deviation)} ⚠️</span>
                      ) : (
                        <span style={{ color: '#059669' }}>✓ Balanced</span>
                      )
                    ) : '—'}
                  </td>
                  <td className={report.has_continuity_issue ? 'negative' : ''} style={{ fontWeight: report.has_continuity_issue ? 'bold' : 'normal' }}>
                    {isOwned ? (
                      index === 0 ? (
                        <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>First Report</span>
                      ) : report.has_continuity_issue ? (
                        <span style={{ color: '#dc2626' }}>{formatCurrency(report.continuity_deviation)} ⚠️</span>
                      ) : (
                        <span style={{ color: '#059669' }}>✓ Matches</span>
                      )
                    ) : '—'}
                  </td>
                  <td>
                    {!isOwned ? (
                      <button
                        onClick={() => {/* TODO: Implement purchase */}}
                        style={{
                          padding: '0.375rem 0.75rem',
                          fontSize: '0.75rem',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                      >
                        Purchase (1 credit)
                      </button>
                    ) : (
                      <span style={{ color: '#059669', fontSize: '0.8rem' }}>Owned</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{
        marginTop: '1rem',
        padding: '0.75rem',
        backgroundColor: '#f9fafb',
        borderRadius: '6px',
        fontSize: '0.85rem',
        color: '#6b7280'
      }}>
        <strong>Legend:</strong>
        <ul style={{ margin: '0.5rem 0 0 1.5rem', paddingLeft: 0 }}>
          <li><strong>Report Type:</strong> Type of report from Box 15 on page 1</li>
          <li><strong>Income:</strong> Total monetary receipts (Line 25)</li>
          <li><strong>Expenses:</strong> Total monetary disbursements (Line 26)</li>
          <li><strong>Internal Δ:</strong> Difference between actual ending and expected (Start + Income - Expenses)</li>
          <li><strong>Prior Δ:</strong> Difference between this report's start and prior report's end</li>
          <li><strong>Amber rows:</strong> Report matches another with identical totals (likely 24-hour report within quarterly period)</li>
          <li style={{ paddingLeft: '0.5rem', borderLeft: '3px solid #9ca3af', fontWeight: '600', color: '#374151' }}>
            <strong>Greyed rows:</strong> Reports exist in system but you don't own them. Purchase with 1 credit to view details.
          </li>
        </ul>
      </div>
    </>
  );

  if (loading) {
    return (
      <div className="analytics-page">
        <div className="analytics-container">
          <div className="loading">Loading analytics...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-page">
        <div className="analytics-container">
          <div className="error-message">
            <h2>Access Restricted</h2>
            <p>{error}</p>
            <button onClick={() => navigate('/dashboard')} className="btn-primary">
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!analyticsData?.has_data) {
    return (
      <div className="analytics-page">
        <div className="analytics-container">
          <div className="analytics-header">
            <h1>Analytics Dashboard</h1>
            <button onClick={() => navigate('/dashboard')} className="btn-secondary">
              Back to Dashboard
            </button>
          </div>
          <div className="no-data-message">
            <p>{analyticsData?.message || 'No data available yet.'}</p>
            <button onClick={() => navigate('/dashboard')} className="btn-primary">
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { committees } = analyticsData;

  // Find the selected committee data (single mode) or aggregate group data (group mode)
  let selectedCommittee = null;

  if (analysisScope === 'group' && selectedGroupId && groupCommitteeIds.length > 0) {
    // Group mode: aggregate data from all committees in the group
    const groupCommittees = committees.filter(c => groupCommitteeIds.includes(c.committee_id));

    console.log('Group Committees for aggregation:', groupCommittees);

    if (groupCommittees.length > 0) {
      // Aggregate totals using correct field names from API (using module-level parseNum helper)
      selectedCommittee = {
        committee_name: selectedGroup?.name || 'Group',
        committee_id: selectedGroupId,
        reports_processed: groupCommittees.reduce((sum, c) => sum + (c.reports_processed || 0), 0),
        cash_donations: groupCommittees.reduce((sum, c) => sum + parseNum(c.cash_donations), 0),
        in_kind_donations: groupCommittees.reduce((sum, c) => sum + parseNum(c.in_kind_donations), 0),
        total_expenses: groupCommittees.reduce((sum, c) => sum + parseNum(c.total_expenses), 0),
        net_balance: groupCommittees.reduce((sum, c) => sum + parseNum(c.net_balance), 0),
        total_donors: groupCommittees.reduce((sum, c) => sum + (c.total_donors || 0), 0),

        // For visualizations, we'll aggregate per-entity data
        top_cash_donors: aggregateTopDonors(groupCommittees, 'top_cash_donors'),
        top_in_kind_donors: aggregateTopDonors(groupCommittees, 'top_in_kind_donors'),
        top_expenses_by_payee: aggregateTopExpenses(groupCommittees),
        top_expenses_by_category: aggregateTopExpensesByCategory(groupCommittees),
        sankey: aggregateSankeyData(groupCommittees),
        cycle_breakdown: aggregateCycleBreakdown(groupCommittees),

        // Store group info for rendering
        _isGroup: true,
        _groupCommittees: groupCommittees,
        _groupMembers: selectedGroup?.members || []
      };
    }
  } else {
    // Single mode: existing behavior
    selectedCommittee = committees?.find(c => c.committee_id === selectedCommitteeId);
  }

  // Debug: log the selected committee data
  console.log('Selected Committee:', selectedCommittee);
  console.log('Analysis Scope:', analysisScope);
  console.log('Has sankey?', !!selectedCommittee?.sankey);
  console.log('Has top_cash_donors?', !!selectedCommittee?.top_cash_donors);
  console.log('Has top_expenses_by_payee?', !!selectedCommittee?.top_expenses_by_payee);

  return (
    <div className="analytics-page">
      <div className="analytics-container">
        <div className="analytics-header">
          <h1>Analytics Dashboard</h1>
          <button onClick={() => navigate('/dashboard')} className="btn-secondary">
            Back to Dashboard
          </button>
        </div>

        {/* Selectors Row */}
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {/* Election Cycle Selector */}
          {analyticsData?.available_cycles && (
            <div className="committee-selector" style={{ flex: '1', minWidth: '250px', position: 'relative' }}>
              <label htmlFor="cycle-select" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                Election Cycle:
                <button
                  type="button"
                  className="cycle-help-icon"
                  onClick={() => setShowCycleHelp(!showCycleHelp)}
                  onBlur={() => setTimeout(() => setShowCycleHelp(false), 200)}
                  aria-label="Help: About Election Cycles"
                  style={{
                    background: '#e5e7eb',
                    border: 'none',
                    borderRadius: '50%',
                    width: '18px',
                    height: '18px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    color: '#6b7280',
                    cursor: 'pointer',
                    padding: 0,
                    lineHeight: 1,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#d1d5db';
                    e.target.style.color = '#374151';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#e5e7eb';
                    e.target.style.color = '#6b7280';
                  }}
                >
                  ?
                </button>
              </label>

              {showCycleHelp && (
                <div
                  className="cycle-help-popover"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    marginTop: '0.5rem',
                    background: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    padding: '1rem',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    zIndex: 1000,
                    maxWidth: '400px',
                    fontSize: '0.875rem',
                    lineHeight: '1.5'
                  }}
                >
                  <strong style={{ display: 'block', marginBottom: '0.5rem', color: '#111827' }}>
                    About Election Cycles
                  </strong>
                  <p style={{ margin: '0 0 0.75rem 0', color: '#374151' }}>
                    Missouri has annual elections in April and November. Reports are grouped by their <strong>filing date</strong> into cycles:
                  </p>
                  <div style={{ marginLeft: '0.5rem', color: '#374151' }}>
                    <p style={{ margin: '0 0 0.5rem 0' }}>
                      <strong>April {new Date().getFullYear()} Election:</strong><br />
                      Nov {new Date().getFullYear() - 1} - Oct {new Date().getFullYear()}
                    </p>
                    <p style={{ margin: '0' }}>
                      <strong>November {new Date().getFullYear()} Election:</strong><br />
                      May {new Date().getFullYear()} - Apr {new Date().getFullYear() + 1}
                    </p>
                  </div>
                  <p style={{ margin: '0.75rem 0 0 0', color: '#6b7280', fontSize: '0.8125rem' }}>
                    This ensures all reports filed for the same election (quarterly, 8 days before, 30 days after) are grouped together.
                  </p>
                </div>
              )}

              <select
                id="cycle-select"
                value={selectedCycle.type && selectedCycle.year ? `${selectedCycle.type}-${selectedCycle.year}` : 'all'}
                onChange={(e) => {
                  if (e.target.value === 'all') {
                    setSelectedCycle({ type: null, year: null });
                  } else {
                    const [type, year] = e.target.value.split('-');
                    setSelectedCycle({ type, year: parseInt(year) });
                  }
                }}
              >
                <option value="all">All Cycles</option>
                {analyticsData.available_cycles.april.map(year => (
                  <option key={`april-${year}`} value={`april-${year}`}>
                    April {year} (Nov {year - 1} - Oct {year})
                  </option>
                ))}
                {analyticsData.available_cycles.november.map(year => (
                  <option key={`november-${year}`} value={`november-${year}`}>
                    November {year} (May {year} - Apr {year + 1})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Entity/Group Selector with Analysis Scope */}
          {committees && committees.length > 0 && (
            <div className="committee-selector" style={{ flex: '1', minWidth: '250px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <label htmlFor="entity-select" style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                  {analysisScope === 'single' ? 'Select Committee:' : 'Select Group:'}
                </label>
                {isProOrHigher(user) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    {analysisScope === 'group' && (
                      <a
                        href="/groups"
                        onClick={(e) => { e.preventDefault(); navigate('/groups'); }}
                        style={{
                          fontSize: '0.8125rem',
                          color: '#2563eb',
                          textDecoration: 'none',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                      >
                        Manage groups
                      </a>
                    )}
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      background: '#f3f4f6',
                      borderRadius: '6px',
                      padding: '2px',
                      gap: '2px'
                    }}>
                      <button
                        type="button"
                        onClick={() => setAnalysisScope('single')}
                        style={{
                          padding: '0.25rem 0.625rem',
                          fontSize: '0.8125rem',
                          fontWeight: '500',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                          background: analysisScope === 'single' ? '#2563eb' : 'transparent',
                          color: analysisScope === 'single' ? 'white' : '#6b7280'
                        }}
                      >
                        Single Entity
                      </button>
                      <button
                        type="button"
                        onClick={() => setAnalysisScope('group')}
                        style={{
                          padding: '0.25rem 0.625rem',
                          fontSize: '0.8125rem',
                          fontWeight: '500',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                          background: analysisScope === 'group' ? '#2563eb' : 'transparent',
                          color: analysisScope === 'group' ? 'white' : '#6b7280'
                        }}
                      >
                        Group
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {analysisScope === 'single' ? (
                <>
                  <input
                    type="search"
                    placeholder="Filter committees..."
                    value={committeeFilter}
                    onChange={(e) => setCommitteeFilter(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.75rem',
                      marginBottom: '0.5rem',
                      fontSize: '0.875rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      boxSizing: 'border-box'
                    }}
                  />
                  <select
                    id="entity-select"
                    value={selectedCommitteeId || ''}
                    onChange={(e) => setSelectedCommitteeId(e.target.value)}
                  >
                    {[...committees]
                      .filter((c) => {
                        const q = committeeFilter.trim().toLowerCase();
                        if (!q) return true;
                        return (
                          (c.committee_name || '').toLowerCase().includes(q) ||
                          (c.mec_id || '').toLowerCase().includes(q)
                        );
                      })
                      .sort((a, b) =>
                        (a.committee_name || '').localeCompare(b.committee_name || '', undefined, { sensitivity: 'base' })
                      )
                      .map((committee) => (
                        <option key={committee.committee_id} value={committee.committee_id}>
                          {committee.committee_name} {committee.mec_id ? `(${committee.mec_id})` : ''}
                        </option>
                      ))}
                  </select>
                </>
              ) : (
                <select
                  id="entity-select"
                  value={selectedGroupId || ''}
                  onChange={(e) => setSelectedGroupId(e.target.value)}
                  disabled={userGroups.length === 0}
                  style={userGroups.length === 0 ? { color: '#9ca3af', cursor: 'not-allowed' } : {}}
                >
                  {userGroups.length === 0 ? (
                    <option>No groups created yet</option>
                  ) : (
                    <>
                      <option value="">Select a group...</option>
                      {userGroups.map((group) => (
                        <option key={group.group_id} value={group.group_id}>
                          {group.name} ({group.member_count} {group.member_count === 1 ? 'entity' : 'entities'})
                        </option>
                      ))}
                    </>
                  )}
                </select>
              )}
            </div>
          )}
        </div>

        {/* Summary Cards for Selected Committee */}
        {selectedCommittee && (
          <>
            <div className="summary-grid">
              {/* Top Row: Key Metrics */}
              <div className="summary-card blue">
                <div className="summary-icon"><FileText size={24} /></div>
                <div className="summary-content">
                  <h3>{selectedCommittee.reports_processed}</h3>
                  <p>Reports</p>
                </div>
              </div>

              <div className="summary-card purple">
                <div className="summary-icon"><Users size={24} /></div>
                <div className="summary-content">
                  <h3>{formatNumber(selectedCommittee.total_donors)}</h3>
                  <p style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', justifyContent: 'center', position: 'relative' }}>
                    Unique Donors
                    <span ref={donorTooltipRef} style={{ position: 'relative', display: 'inline-flex' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDonorTooltip(!showDonorTooltip);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          color: 'rgba(255, 255, 255, 0.8)',
                          transition: 'color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 1)'}
                        onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)'}
                        aria-label="More information about donor counts"
                      >
                        <Info size={16} />
                      </button>
                      {showDonorTooltip && (
                        <div style={{
                          position: 'absolute',
                          bottom: '100%',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          marginBottom: '8px',
                          backgroundColor: 'white',
                          color: '#374151',
                          padding: '0.75rem 1rem',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                          fontSize: '0.875rem',
                          lineHeight: '1.5',
                          maxWidth: '300px',
                          width: 'max-content',
                          zIndex: 1000,
                          textAlign: 'left'
                        }}>
                          Counts include named donors with itemized contributions, per Missouri Ethics Commission reporting rules.
                        </div>
                      )}
                    </span>
                  </p>
                </div>
              </div>

              {/* Financial Metrics */}
              <div className="summary-card green">
                <div className="summary-icon"><DollarSign size={24} /></div>
                <div className="summary-content">
                  <h3>{formatCurrency(selectedCommittee.cash_donations)}</h3>
                  <p>Cash Donations</p>
                </div>
              </div>

              <div className="summary-card teal">
                <div className="summary-icon"><Gift size={24} /></div>
                <div className="summary-content">
                  <h3>{formatCurrency(selectedCommittee.in_kind_donations)}</h3>
                  <p>In-Kind Donations</p>
                </div>
              </div>

              <div className="summary-card red">
                <div className="summary-icon"><TrendingDown size={24} /></div>
                <div className="summary-content">
                  <h3>{formatCurrency(selectedCommittee.total_expenses)}</h3>
                  <p>Total Expenses</p>
                </div>
              </div>

              <div className={`summary-card ${selectedCommittee.net_balance >= 0 ? 'green' : 'yellow'}`}>
                <div className="summary-icon">
                  {selectedCommittee.net_balance >= 0 ? <CheckCircle size={24} /> : <AlertTriangle size={24} />}
                </div>
                <div className="summary-content">
                  <h3>{formatCurrency(selectedCommittee.net_balance)}</h3>
                  <p>Net Balance</p>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem', fontWeight: 'normal' }}>
                    Net of reported income and expenses across selected reports
                  </div>
                </div>
              </div>
            </div>

            {/* About this dashboard info block */}
            <div style={{
              marginTop: '2rem',
              padding: '1rem 1.25rem',
              fontSize: '0.9rem',
              lineHeight: '1.6',
              color: '#374151'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '0.5rem', fontSize: '0.95rem' }}>
                About this dashboard
              </div>
              <p style={{ margin: 0 }}>
                This page summarizes campaign finance data extracted directly from Missouri Ethics Commission filings you uploaded. All figures reflect reported amounts. Analytical views apply consistent calculations without interpretation or judgment.
              </p>
            </div>
          </>
        )}

        {/* Election Cycle Breakdown Table */}
        {!selectedCycle.type && selectedCommittee?.cycle_breakdown && (
          <div className="analytics-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ margin: 0, paddingBottom: 0, border: 'none' }}>Election Cycle Breakdown</h2>
              <select
                value={cycleTableView}
                onChange={(e) => setCycleTableView(e.target.value)}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '0.9rem',
                  cursor: 'pointer'
                }}
              >
                <option value="april">April Cycles</option>
                <option value="november">November Cycles</option>
              </select>
            </div>

            <div className="table-container">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Year</th>
                    <th>Reports</th>
                    <th>Donors</th>
                    <th>Cash Donations</th>
                    <th>In-Kind Donations</th>
                    <th>Loans</th>
                    <th>Total Donations</th>
                    <th>Expenses</th>
                    <th>Net Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedCommittee.cycle_breakdown[cycleTableView].map((cycle) => (
                    <tr key={`${cycleTableView}-${cycle.year}`}>
                      <td className="committee-name">
                        {cycleTableView === 'april'
                          ? `April ${cycle.year}`
                          : `November ${cycle.year}`}
                      </td>
                      <td>{cycle.reports}</td>
                      <td>{formatNumber(cycle.donors)}</td>
                      <td className="amount">{formatCurrency(cycle.cash_donations)}</td>
                      <td className="amount">{formatCurrency(cycle.in_kind_donations)}</td>
                      <td className="amount">{formatCurrency(cycle.loans || 0)}</td>
                      <td className="amount">{formatCurrency(cycle.total_donations)}</td>
                      <td className="amount">{formatCurrency(cycle.total_expenses)}</td>
                      <td className={cycle.net_balance >= 0 ? 'positive' : 'negative'}>
                        {formatCurrency(cycle.net_balance)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Sankey Chart: Money Flow */}
        {selectedCommittee?.sankey && (() => {
          // Check if there's any meaningful data
          const hasData = (
            (selectedCommittee.sankey.top_cash_donors?.length > 0) ||
            (selectedCommittee.sankey.top_in_kind_donors?.length > 0) ||
            (selectedCommittee.sankey.top_expense_payees?.length > 0) ||
            (selectedCommittee.sankey.other_cash_donations > 0) ||
            (selectedCommittee.sankey.other_in_kind_donations > 0) ||
            (selectedCommittee.sankey.other_expenses > 0) ||
            (selectedCommittee.sankey.net_cash_balance > 0) ||
            (selectedCommittee.sankey.total_in_kind_donations > 0) ||
            (selectedCommittee.sankey.total_loans > 0)
          );

          return (
            <div className="analytics-section">
              <h2>Campaign Finance Flow</h2>
              {!hasData ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280', fontSize: '1rem' }}>
                  No transaction data exists for current period
                </div>
              ) : (
                <>
                  <div style={{ width: '100%', height: '680px' }}>
                    <Chart
                      chartType="Sankey"
                      width="100%"
                      height="680px"
                      data={(() => {
                        // Group mode: use pre-computed chart data
                        if (selectedCommittee.sankey._isGroupMode) {
                          return selectedCommittee.sankey._chartData;
                        }

                        // Single entity mode: build chart data from API structure
                        const tooltipColumn = { role: 'tooltip', type: 'string', p: { html: true } };
                        const data = [['From', 'To', 'Amount', tooltipColumn]];
                        const entityName = selectedCommittee.committee_name || 'Entity';
                        const middleLabel = (name, kind) => `${kind} — ${name}`;
                        const cashNode = middleLabel(entityName, 'Cash');
                        const inKindCenterNode = middleLabel(entityName, 'In-Kind');
                        const inKindRightNode = 'In-Kind ';  // Note the space to make it a different node

                        // Helper function to round amounts to 2 decimal places and handle null/undefined
                        const roundAmount = (amount) => {
                          const num = Number(amount);
                          return isNaN(num) || num === null || num === undefined ? 0 : Math.round(num * 100) / 100;
                        };
                        const buildTooltip = (from, to, amount) => (
                          `<div><strong>${from} → ${to}</strong><br/>Amount: <strong>${formatUSD(amount)}</strong></div>`
                        );
                        const pushRow = (from, to, amount) => {
                          const value = roundAmount(amount);
                          if (value > 0) {
                            data.push([from, to, value, buildTooltip(from, to, value)]);
                          }
                        };

                        // Ensure arrays exist
                        const topCashDonors = selectedCommittee.sankey.top_cash_donors || [];
                        const topInKindDonors = selectedCommittee.sankey.top_in_kind_donors || [];
                        const topExpensePayees = selectedCommittee.sankey.top_expense_payees || [];

                        // CASH DONATIONS -> Cash (donors on left)
                        // Top 5 cash donors individually
                        topCashDonors.forEach(donor => {
                          if (donor && donor.amount && donor.amount > 0) {
                            pushRow(donor.name, cashNode, donor.amount);
                          }
                        });

                        // Other cash donations (if any)
                        if (selectedCommittee.sankey.other_cash_donations > 0) {
                          pushRow('Other Donors', cashNode, selectedCommittee.sankey.other_cash_donations);
                        }

                        // LOANS -> Cash (loans flow into cash pool)
                        if (selectedCommittee.sankey.total_loans > 0) {
                          pushRow('Loans', cashNode, selectedCommittee.sankey.total_loans);
                        }

                        // EXPENSES (only from Cash node) - Cash flows to payees on right
                        // Top 5 expense payees individually
                        topExpensePayees.forEach(payee => {
                          if (payee && payee.amount && payee.amount > 0) {
                            pushRow(cashNode, payee.name, payee.amount);
                          }
                        });

                        // Other expenses (if any)
                        if (selectedCommittee.sankey.other_expenses > 0) {
                          pushRow(cashNode, 'Other Expenses', selectedCommittee.sankey.other_expenses);
                        }

                        // CASH BALANCE - Added after expenses so it's the bottommost cash outlay
                        if (selectedCommittee.sankey.net_cash_balance > 0) {
                          pushRow(cashNode, 'Cash Balance', selectedCommittee.sankey.net_cash_balance);
                        }

                        // IN-KIND DONATIONS -> In-Kind (center) -> In-Kind (right terminal)
                        // Top 5 in-kind donors individually
                        topInKindDonors.forEach(donor => {
                          if (donor && donor.amount && donor.amount > 0) {
                            pushRow(donor.name, inKindCenterNode, donor.amount);
                          }
                        });

                        // Other in-kind donations (if any)
                        if (selectedCommittee.sankey.other_in_kind_donations > 0) {
                          pushRow('Other In-Kind', inKindCenterNode, selectedCommittee.sankey.other_in_kind_donations);
                        }

                        // In-Kind (center) -> In-Kind (right terminal) - total in-kind value
                        const totalInKind = (selectedCommittee.sankey.total_in_kind_donations || 0);
                        if (totalInKind > 0) {
                          pushRow(inKindCenterNode, inKindRightNode, totalInKind);
                        }

                        return data;
                      })()}
                      options={{
                        sankey: {
                          node: {
                            label: {
                              fontSize: 12,
                              color: '#111827',
                              bold: true
                            },
                            width: 18,
                            nodePadding: 26,
                            interactivity: true
                          },
                          link: {
                            colorMode: 'gradient',
                            color: {
                              fill: '#d1d5db',
                              fillOpacity: 0.5
                            }
                          },
                          iterations: 0  // Disable automatic layout optimization to preserve order
                        },
                        tooltip: {
                          isHtml: true,
                          textStyle: {
                            fontSize: 13
                          }
                        }
                      }}
                      formatters={[
                        {
                          type: 'NumberFormat',
                          column: 2,
                          options: {
                            prefix: '$',
                            pattern: '#,###.##'
                          }
                        }
                      ]}
                    />
                  </div>
                  <p style={{ textAlign: 'center', color: '#6b7280', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                    Visual representation of campaign finance flow. Left: Donors and loans. Center: Cash and In-Kind pools. Right: Cash flows to expenses and balance; In-Kind flows to terminal node representing non-cash value contributed. Top 5 donors and expense payees shown individually.
                  </p>
                </>
              )}
            </div>
          );
        })()}

        {/* Top Donors - Side by Side */}
        {(selectedCommittee?.top_cash_donors || selectedCommittee?.top_in_kind_donors) && (
          <div className="two-column-grid">
            {/* Top Cash Donors */}
            <div className="analytics-section">
              <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', position: 'relative' }}>
                Top 10 Cash Donors
                <span ref={cashDonorTooltipRef} style={{ position: 'relative', display: 'inline-flex' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowCashDonorTooltip(!showCashDonorTooltip);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      color: '#6b7280',
                      transition: 'color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#374151'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#6b7280'}
                    aria-label="More information about donor counts"
                  >
                    <Info size={16} />
                  </button>
                  {showCashDonorTooltip && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: '0',
                      marginTop: '8px',
                      backgroundColor: 'white',
                      color: '#374151',
                      padding: '0.75rem 1rem',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      fontSize: '0.875rem',
                      lineHeight: '1.5',
                      maxWidth: '300px',
                      width: 'max-content',
                      zIndex: 1000
                    }}>
                      Counts include named donors with itemized contributions, per Missouri Ethics Commission reporting rules.
                    </div>
                  )}
                </span>
              </h2>
              {selectedCommittee.top_cash_donors?.length > 0 ? (
                <div className="table-container">
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Donor Name</th>
                        <th>Total Amount</th>
                        <th>Contributions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedCommittee.top_cash_donors.map((donor, index) => (
                        <tr key={index}>
                          <td className="rank">{index + 1}</td>
                          <td className="committee-name">
                            {donor.name}
                            {donor.address && (
                              <div style={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: 'normal' }}>
                                {donor.address}
                              </div>
                            )}
                          </td>
                          <td className="amount">{formatCurrency(donor.total_amount)}</td>
                          <td>{donor.contribution_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280', fontSize: '1rem' }}>
                  No transaction data exists for current period
                </div>
              )}
            </div>

            {/* Top In-Kind Donors */}
            <div className="analytics-section">
              <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', position: 'relative' }}>
                Top 10 In-Kind Donors
                <span ref={inKindDonorTooltipRef} style={{ position: 'relative', display: 'inline-flex' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowInKindDonorTooltip(!showInKindDonorTooltip);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      color: '#6b7280',
                      transition: 'color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#374151'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#6b7280'}
                    aria-label="More information about donor counts"
                  >
                    <Info size={16} />
                  </button>
                  {showInKindDonorTooltip && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: '0',
                      marginTop: '8px',
                      backgroundColor: 'white',
                      color: '#374151',
                      padding: '0.75rem 1rem',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      fontSize: '0.875rem',
                      lineHeight: '1.5',
                      maxWidth: '300px',
                      width: 'max-content',
                      zIndex: 1000
                    }}>
                      Counts include named donors with itemized contributions, per Missouri Ethics Commission reporting rules.
                    </div>
                  )}
                </span>
              </h2>
              {selectedCommittee.top_in_kind_donors?.length > 0 ? (
                <div className="table-container">
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Donor Name</th>
                        <th>Total Amount</th>
                        <th>Contributions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedCommittee.top_in_kind_donors.map((donor, index) => (
                        <tr key={index}>
                          <td className="rank">{index + 1}</td>
                          <td className="committee-name">
                            {donor.name}
                            {donor.address && (
                              <div style={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: 'normal' }}>
                                {donor.address}
                              </div>
                            )}
                          </td>
                          <td className="amount">{formatCurrency(donor.total_amount)}</td>
                          <td>{donor.contribution_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280', fontSize: '1rem' }}>
                  No transaction data exists for current period
                </div>
              )}
            </div>
          </div>
        )}

        {/* Top Expenses - Side by Side */}
        {(selectedCommittee?.top_expenses_by_payee || selectedCommittee?.top_expenses_by_category) && (
          <div className="two-column-grid">
            {/* Top Expenses by Payee */}
            <div className="analytics-section">
              <h2>Top 10 Expenses by Payee</h2>
              {selectedCommittee.top_expenses_by_payee?.length > 0 ? (
                <div className="table-container">
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Payee</th>
                        <th>Total Amount</th>
                        <th>Transactions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedCommittee.top_expenses_by_payee.map((expense, index) => (
                        <tr key={index}>
                          <td className="rank">{index + 1}</td>
                          <td className="committee-name">{expense.payee}</td>
                          <td className="amount">{formatCurrency(expense.total_amount)}</td>
                          <td>{expense.expense_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280', fontSize: '1rem' }}>
                  No transaction data exists for current period
                </div>
              )}
            </div>

            {/* Top Expenses by Category */}
            <div className="analytics-section">
              <h2>Top 10 Expenses by Category</h2>
              <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '1rem', marginTop: '-0.5rem' }}>
                Expense categories reflect descriptions as reported by the committee and are grouped for readability.
              </p>
              {selectedCommittee.top_expenses_by_category?.length > 0 ? (
                <div className="table-container">
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Category</th>
                        <th>Total Amount</th>
                        <th>Transactions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedCommittee.top_expenses_by_category.map((expense, index) => (
                        <tr key={index}>
                          <td className="rank">{index + 1}</td>
                          <td className="committee-name">{expense.category}</td>
                          <td className="amount">{formatCurrency(expense.total_amount)}</td>
                          <td>{expense.expense_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280', fontSize: '1rem' }}>
                  No transaction data exists for current period
                </div>
              )}
            </div>
          </div>
        )}

        {/* Owned Reports Table - Cash Flow Integrity */}
        {selectedCommittee && (
          <div className="analytics-section">
            <h2>Report Cash Flow Integrity</h2>
            {loadingReports ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                Loading report details...
              </div>
            ) : committeeReports && committeeReports.mode === 'group' ? (
              // Group mode: render per-entity integrity sections
              <>
                {selectedCommittee._groupMembers && selectedCommittee._groupMembers.length > 0 && (
                  <div style={{ marginBottom: '1.5rem', fontSize: '0.9rem', color: '#6b7280' }}>
                    Showing integrity checks for {selectedCommittee._groupMembers.length} {selectedCommittee._groupMembers.length === 1 ? 'entity' : 'entities'} in this group
                  </div>
                )}
                {Object.entries(committeeReports.by_entity || {}).map(([committeeId, entityReports]) => {
                  const memberInfo = selectedCommittee._groupMembers?.find(m => m.committee_id === committeeId);
                  const entityName = memberInfo?.committee_name || committeeId;

                  return entityReports?.reports?.length > 0 ? (
                    <div key={committeeId} style={{ marginBottom: '2.5rem' }}>
                      <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '0.75rem', color: '#374151' }}>
                        {entityName}
                      </h3>
                      <div style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#6b7280' }}>
                        <strong>{entityReports.owned_reports}</strong> owned reports,
                        <strong> {entityReports.unowned_reports}</strong> unowned reports available
                      </div>
                      {renderIntegrityTable(entityReports.reports, entityReports)}
                    </div>
                  ) : null;
                })}
              </>
            ) : committeeReports?.reports?.length > 0 ? (
              // Single mode: existing behavior
              <>
                <div style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#6b7280' }}>
                  <strong>{committeeReports.owned_reports}</strong> owned reports,
                  <strong> {committeeReports.unowned_reports}</strong> unowned reports available
                </div>
                {renderIntegrityTable(committeeReports.reports, committeeReports)}
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
                No report data available for this committee.
              </div>
            )}
          </div>
        )}

        {/* Note for users */}
        <div style={{
          background: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: '10px',
          padding: '1rem 1.5rem',
          marginTop: '1rem'
        }}>
          <p style={{ margin: 0, color: '#1e40af', fontSize: '0.9rem' }}>
            <strong>Note:</strong> Analytics are shown per committee. Use the dropdown above to switch between committees.
            Donor counts include only named donors with contributions ≥ $100. Donor and expense deduplication is performed based on name and address matching.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
